import React, { useState } from 'react';
import {
    IMAGE_MAX_SIZE_ALLOWED,
    IMAGE_MAX_SIZE_ALLOWED_MB,
    IMAGE_TYPE_KEY_ROOM,
    MIN_IMAGE_WIDTH,
    MIN_IMAGE_HEIGHT,
    IMAGE_TO_UPLOAD_INITIAL_STATE,
} from '../../../../utils/zitoneoServicesUtils';
import LateralPanel from '../../../../../../muiComponents/LateralPanel';
import Icon from '../../../../../../muiComponents/Icon';
import Title from '../../../../../ownerPortal/yourProperties/Title';
import LoadedOptionsTypeahead from '../../../../../../muiComponents/LoadedOptionsTypeahead';
import { useZitoneoImagesPopUp } from '../../../../contexts/ZitoneoImagesContext';
import { useZitoneoHotelRooms } from '../../../../contexts/ZitoneoContentContext';

import {
    ComplementaryHotelImageTypeState,
    LocalHotelImageState,
    HotelRoomState,
} from '../../../../types/zitoneoServicesTypes';
import ImageDropZone from '../../../../../ownerPortal/yourProperties/ImageDropZone';
import { Alert } from '@mui/material';
import ImageItemView from './ImageItemView';
import useTranslate from '../../../../../../customHooks/useTranslate';
import Button from '../../../../../../muiComponents/Button';
import LoadingPlaceholder from '../../../../../../muiComponents/LoadingPlaceholder';

interface AddZitoneoImagePanelProps {
    open?: boolean;
    onClose: () => void;
}

const AddZitoneoImagePanel = ({ open, onClose }: AddZitoneoImagePanelProps) => {
    const {
        hotelImageTypes,
        selectedImageType,
        handleSelectImageType,
        selectedRoom,
        handleSelectRoom,
        images,
        handleAddImages,
        handleUpdateImage,
        handleDeleteImages,
        isUploadingImages,
        saveImagesToDDBB,
    } = useZitoneoImagesPopUp();
    const { rooms } = useZitoneoHotelRooms();
    const [errorMessage, setErrorMessage] = useState<string>('');
    const translate = useTranslate();
    const canSave =
        !!selectedImageType &&
        images.some(image => image.File) &&
        (selectedImageType.Key !== IMAGE_TYPE_KEY_ROOM ||
            (selectedRoom?.SupplierHotelRooms[0]?.IdSupplierHotelRoom || 0) >
                0);

    const handleClose = () => {
        if (!isUploadingImages) {
            onClose();
        }
    };

    return (
        <LateralPanel open={open} className=' flex-column'>
            <header className='flex flex-between flex-align-center padding-inline50'>
                <Title text='add_images' className='text-secondary-color' />
                <Icon
                    iconName='Close'
                    className='cursor-pointer'
                    onClick={handleClose}
                />
            </header>
            <main className='flex-column gap-30 height100perc overflow-y-auto padding-inline50'>
                {isUploadingImages ? (
                    <LoadingPlaceholder
                        variant='rounded'
                        width='100%'
                        height={200}
                    />
                ) : (
                    <>
                        <LoadedOptionsTypeahead
                            labelText='image_category'
                            options={hotelImageTypes}
                            optionsLabelKey={'Name'}
                            getOptionLabel={(
                                option: ComplementaryHotelImageTypeState
                            ) => option.Name}
                            isOptionEqualToValue={(
                                option: ComplementaryHotelImageTypeState,
                                value: ComplementaryHotelImageTypeState
                            ) => option.Id === value.Id}
                            value={selectedImageType}
                            handleChange={(
                                _event: React.SyntheticEvent,
                                value: ComplementaryHotelImageTypeState | null
                            ) => {
                                handleSelectImageType(value);
                            }}
                            className='margin-top10'
                        />
                        {selectedImageType?.Key === IMAGE_TYPE_KEY_ROOM && (
                            <LoadedOptionsTypeahead
                                labelText='room'
                                options={rooms}
                                optionsLabelKey='Label'
                                getOptionLabel={(option: HotelRoomState) =>
                                    option.Label
                                }
                                isOptionEqualToValue={(
                                    option: HotelRoomState,
                                    value: HotelRoomState
                                ) => option.Id === value.Id}
                                value={selectedRoom}
                                handleChange={(
                                    _event: React.SyntheticEvent,
                                    value: HotelRoomState | null
                                ) => {
                                    handleSelectRoom(value);
                                }}
                                className='margin-top10'
                            />
                        )}
                        <ImageDropZone
                            disabled={!selectedImageType}
                            disabledMessage='image_category_first'
                            maxSizeAllowed={IMAGE_MAX_SIZE_ALLOWED}
                            maxSizeAllowedMB={IMAGE_MAX_SIZE_ALLOWED_MB}
                            minWidthAllowed={MIN_IMAGE_WIDTH}
                            minHeightAllowed={MIN_IMAGE_HEIGHT}
                            uploadFiles={(files: File[]) => {
                                const filesWithPreview: LocalHotelImageState[] =
                                    files.map((file: File) => ({
                                        ...IMAGE_TO_UPLOAD_INITIAL_STATE,
                                        Id: Math.random()
                                            .toString(36)
                                            .substr(2, 9),
                                        IdCategory: selectedImageType?.Id
                                            ? parseInt(selectedImageType.Id)
                                            : 0,
                                        Category: selectedImageType?.Name || '',
                                        CategoryKey:
                                            selectedImageType?.Key || '',
                                        Caption: file.name,
                                        //    IdHotelRoomType: selectedImageType?.Key === IMAGE_TYPE_KEY_ROOM
                                        File: file,
                                        Published: true,
                                    }));
                                handleAddImages(filesWithPreview);
                            }}
                            setErrorMessage={(message: string) => {
                                setErrorMessage(message);
                            }}
                        />
                        {errorMessage ? (
                            <Alert
                                severity='error'
                                onClose={() => setErrorMessage('')}
                            >
                                {errorMessage}
                            </Alert>
                        ) : (
                            ''
                        )}
                        {images.length ? (
                            <ul className='zitoneo-images-tab__list'>
                                {images.map(image => (
                                    <ImageItemView
                                        key={image.Id}
                                        src={image.Url}
                                        caption={image.Caption || ''}
                                        category={
                                            image.CategoryKey ===
                                            IMAGE_TYPE_KEY_ROOM
                                                ? translate('room')
                                                : image.Category
                                        }
                                        feature={image.Feature}
                                        published={image.Published}
                                        onCaptionChange={caption =>
                                            handleUpdateImage(image.Id, {
                                                Caption: caption,
                                            })
                                        }
                                        onFeatureChange={feature =>
                                            handleUpdateImage(image.Id, {
                                                Feature: feature,
                                            })
                                        }
                                        onPublishedChange={published =>
                                            handleUpdateImage(image.Id, {
                                                Published: published,
                                            })
                                        }
                                        onDelete={() =>
                                            handleDeleteImages(image.Id)
                                        }
                                    />
                                ))}
                            </ul>
                        ) : (
                            ''
                        )}
                    </>
                )}
            </main>
            <footer
                className={`flex ${
                    images.length ? 'flex-between' : 'flex-end'
                } padding-inline50`}
            >
                {images.length > 0 && (
                    <Button
                        variant='contained'
                        className={isUploadingImages ? '' : 'delete-button'}
                        disabled={isUploadingImages}
                        onClick={() => handleDeleteImages()}
                    >
                        {translate('delete_all_images')}
                    </Button>
                )}
                <div className='flex gap-20'>
                    <Button
                        variant='outlined'
                        disabled={isUploadingImages}
                        onClick={handleClose}
                    >
                        {translate('cancel')}
                    </Button>
                    <Button
                        variant='contained'
                        disabled={isUploadingImages || !canSave}
                        onClick={() => saveImagesToDDBB()}
                    >
                        {translate('save')}
                    </Button>
                </div>
            </footer>
        </LateralPanel>
    );
};

export default AddZitoneoImagePanel;
